import React from 'react';
import { css } from '@emotion/react';
import { i18n_build_your_routine_track_your_progress_meet_your_goals } from '@yi/i18n/phrases/i18n_build_your_routine_track_your_progress_meet_your_goals';
import { i18n_stay_motivated_by_customizing_and_tracking_your_daily_yoga_routine_collect_certificates_for_and } from '@yi/i18n/phrases/i18n_stay_motivated_by_customizing_and_tracking_your_daily_yoga_routine_collect_certificates_for_and';

import { getOptimizedImgSrc } from '~/src/common/image';
import { IconAppleHealthkitLogo } from '~/src/components/icons';
import { useLocale } from '~/src/components/LocaleProvider';
import { breakpoints } from '~/src/styles';
import { paddingVariables, typography } from '~/src/styles/design-system';
import { resetStyles } from '~/src/styles/reset-styles-mixins';

import { ReactComponent as Mandala } from './mandala.svg';
import { commonStyles } from '../common-styles';
import { StartFreeTrialButton } from '../StartFreeTrialButton';

const IMG_SRC =
  'https://brooklyn.gaia.com/v1/assets-render/de5787c5-736d-49fe-982e-5250c74c8ac7?hash=c5309a93e9a10f0e6fac0e3003892d672eca39d63d823a5121f2d39cbdb665f4';

const desktopMandalaWidth = '380px';
const desktopPaddingBetweenMandalaAndTitle = '60px';

const MAX_IMG_WIDTH = 375;

const styles = {
  container: css({
    display: 'grid',
    gridTemplateAreas: `'img' 'img' '.' 'appleHealthKit' '.' 'title' '.' 'details' '.' 'button' '.'`,
    gridTemplateRows: `1fr 1fr ${paddingVariables.p24} auto ${paddingVariables.p40} auto ${paddingVariables.p16} auto ${paddingVariables.p56} auto ${paddingVariables.p56}`,
    [breakpoints.minTablet]: {
      gridTemplateRows: `1fr 1fr ${paddingVariables.p32} auto ${paddingVariables.p56} auto ${paddingVariables.p24} auto ${paddingVariables.p80} auto ${paddingVariables.p80}`,
    },
    [breakpoints.minDesktop]: {
      gridTemplateAreas: `
        '. . . . .'
        '. img . title .'
        '. . . . .'
        '. appleHealthKit . . .'
        '. . . . .'
        '. details details details .'
        '. . . . .'
        '. button button button .'
        '. . . . .'
      `,
      gridTemplateRows: `${paddingVariables.p120} auto ${paddingVariables.p16} auto ${paddingVariables.p80} auto ${paddingVariables.p80} auto ${paddingVariables.p120}`,
      gridTemplateColumns: `1fr ${desktopMandalaWidth} ${desktopPaddingBetweenMandalaAndTitle} calc(var(--Max-Desktop-Width) - ${desktopMandalaWidth} - ${desktopPaddingBetweenMandalaAndTitle} ) 1fr`,
    },
  }),
  background: css({
    zIndex: 1,
    gridArea: 'background',
    borderRadius: 'var(--Border-Radius)',
    background: 'var(--Primary-Background, rgba(255, 243, 226, 0.50))',
    gridRowStart: '2',
    gridRowEnd: '-1',
    gridColumnStart: '1',
    [breakpoints.minDesktop]: {
      gridRowStart: '1',
    },
  }),
  button: css(commonStyles.contentPadding, {
    gridArea: 'button',
  }),
  title: css(commonStyles.contentPadding, typography.heading1, {
    zIndex: 10,
    gridArea: 'title',
    color: 'var(--Dark-Text, #27292D)',
    [breakpoints.minDesktop]: {
      alignSelf: 'center',
    },
  }),
  details: css(resetStyles.heading, commonStyles.contentPadding, typography.bodyText, {
    zIndex: 10,
    color: 'var(--Grey-Text, #585858)',
    gridArea: 'details',
    margin: 0,
    [breakpoints.minDesktop]: [
      typography.subHeading1,
      {
        color: 'var(--Grey-Text, #585858)',
      },
    ],
  }),
  img: css({
    gridArea: 'img',
    zIndex: '10',
    position: 'relative',
    width: '70%',
    maxWidth: `${MAX_IMG_WIDTH}px`,
    justifySelf: 'center',
    [breakpoints.minDesktop]: {
      alignSelf: 'center',
      width: '85%',
    },
  }),
  mandala: css({
    opacity: 0.5,
    width: '85%',
    zIndex: '5',
    margin: 'auto',
    gridRowStart: '1',
    gridRowEnd: '3',
    gridColumnStart: '1',
    maxWidth: '450px',
    [breakpoints.minDesktop]: {
      gridRowStart: '2',
      gridRowEnd: '3',
      gridColumnStart: '2',
      width: '100%',
      alignSelf: 'center',
    },
  }),
  applyHealthKit: css({
    zIndex: 10,
    height: '34px',
    gridArea: 'appleHealthKit',
    justifySelf: 'center',
  }),
};

export const BuildYourRoutine: React.FC = () => {
  const { locale } = useLocale();

  return (
    <section css={styles.container}>
      <div css={styles.background} />
      <img
        css={styles.img}
        src={getOptimizedImgSrc({ src: IMG_SRC, width: MAX_IMG_WIDTH * 2, quality: 50 })}
        loading="lazy"
        decoding="async"
      />
      <Mandala css={styles.mandala} />
      <IconAppleHealthkitLogo css={styles.applyHealthKit} />
      <h1 css={styles.title}>
        {i18n_build_your_routine_track_your_progress_meet_your_goals(locale)}
      </h1>
      <h2 css={styles.details}>
        {i18n_stay_motivated_by_customizing_and_tracking_your_daily_yoga_routine_collect_certificates_for_and(
          locale,
        )}
      </h2>
      <StartFreeTrialButton css={styles.button} />
    </section>
  );
};
